/**
 * The route a plate item takes, from the crate it starts in to the plate.
 *
 * A ticket shows the *destination* — "a bowl containing stewed carrot, stewed
 * onion, stewed mushroom" — and never the *route*: that you take a carrot from
 * a crate, chop it on a board, cook it in a pan on a stove and plate it, three
 * times over. For an expert that is signal-cleaning. For a first-timer it
 * deletes the single most important step, and "I do not know what to do next"
 * is the most reported thing about this game.
 *
 * Nothing here is new data. `RequiredItem.stations` already arrives ordered
 * crate-to-plate and `container` already survives the trip from `recipes.json`
 * (the server derives both in `dishTicket`); the renderer simply threw them
 * away. This file linearises what is already on the wire.
 *
 * Note what it deliberately does *not* carry: durations. They are not on the
 * wire and the strip does not want them — it answers *what next*, not *how
 * long*, and *how long* is already answered live by the station's own progress
 * bar. Adding them here would leak `timings.json` into the client for no gain.
 *
 * Pure by construction: no canvas, no React, no config. `hud.tsx` turns a
 * `RouteStep[]` into icons; every decision about what the steps *are* is here,
 * and `client/test/route.test.ts` asserts it against the shipped recipes.
 */

import type {
  ContainerKind,
  DishRequirement,
  IngredientId,
  RequiredItem,
  StationType,
} from '@overcooked/shared'

/**
 * One hop on the route.
 *
 * Four kinds rather than one polymorphic blob, because the renderer draws each
 * with a different function — `drawIngredient`, `drawStationBadge`,
 * `drawContainer`, `drawPlate` — and picking between them on a `kind` is how
 * the strip stays exhaustive when a fifth kind is added.
 */
export interface RouteStep {
  kind: 'source' | 'station' | 'container' | 'plate'
  /** Set on `source`: the crate ingredient this item descends from. */
  ingredient?: IngredientId
  /** Set on `station`: the equipment this hop passes through. */
  station?: StationType
  /** Set on `container`: the vessel the cooking happens in. */
  container?: ContainerKind
}

/**
 * One item's route, forward.
 *
 * Source, then every station it passes through in the order the server listed
 * them, then the vessel its cooking happens in, then the plate. A raw item —
 * one that goes from crate to plate untouched — is two steps, and that is a
 * true statement about it rather than a degenerate case to guard against.
 */
export function routeFor(item: RequiredItem): RouteStep[] {
  const steps: RouteStep[] = [{ kind: 'source', ingredient: item.source }]
  for (const station of item.stations) steps.push({ kind: 'station', station })
  if (item.container) steps.push({ kind: 'container', container: item.container })
  steps.push({ kind: 'plate' })
  return steps
}

/**
 * A dish's routes, deduplicated.
 *
 * `DishRequirement.items` keeps repeats as repeats — three stewed vegetables
 * are three icons on the badge row — but three *identically routed* items are
 * one instruction, and printing "crate → board → pan → stove → plate" three
 * times teaches nothing it does not teach once. The badge row above the strip
 * still shows the count; this shows the method.
 *
 * Order is the dish's own, by first appearance, so the strip reads in the order
 * the ticket's icons do.
 */
export function routesFor(requires: DishRequirement): RouteStep[][] {
  const seen = new Set<string>()
  const routes: RouteStep[][] = []
  for (const item of requires.items) {
    const route = routeFor(item)
    const key = routeKey(route)
    if (seen.has(key)) continue
    seen.add(key)
    routes.push(route)
  }
  return routes
}

/**
 * A stable identity for a route, for deduplication and for React keys.
 *
 * Built from the fields each kind actually uses rather than from `JSON.stringify`,
 * so an undefined-versus-absent difference in a hand-built fixture cannot make
 * two identical routes look distinct.
 */
export function routeKey(route: RouteStep[]): string {
  return route.map((step) => `${step.kind}:${step.ingredient ?? step.station ?? step.container ?? ''}`).join('>')
}
